<template>
	<HeaderWrapper>
		<div
			v-if="loading"
			class="flex items-start gap-2"
		>
			{{ t('newSession.loadingPreviousPlans') }}
			<LoadingSVG class="h-4! w-4!" />
		</div>
		<div
			v-else
			@click="showSelectionModal = true"
			aria-label="showPlanSelection"
			class="cursor-pointer"
		>
			<HeaderText :selected-plan-option="selectedPlanOption" />
		</div>
		<div v-if="selectedPlanOption?.lastUpdated">
			<!--	this button should start a new plan -->
			<button
				@click.prevent="startNewPlan"
				class="align-text-top text-xs text-slate-500 italic underline"
			>
				{{ t('newSession.startNew') }}
			</button>
		</div>
	</HeaderWrapper>

	<PrecoaModal
		class="sm:w-[30rem]! sm:max-w-none"
		size="large"
		v-model:open="showSelectionModal"
		@close="showSelectionModal = false"
	>
		<SelectPlanModalContent
			:plans="importedPlanOptions"
			:initialSelection="selectedPlanOption"
			:selectedPriceBook="selectedPriceBook"
			@planSelected="handleSelectedPlanChange"
			@cancel="showSelectionModal = false"
		/>
	</PrecoaModal>
</template>

<script setup lang="ts">
import type { PriceBook } from '@/classes/PriceBook'
import LoadingSVG from '@/components/common/LoadingSVG.vue'
import PrecoaModal from '@/components/common/PrecoaModal.vue'
import { useCloudSyncService } from '@/services/CloudSync/cloudSyncService'
import { type StoredPlanData, usePlanStore } from '@/stores/plan'
import type { PlanData, PlanOption } from '@/types'
import HeaderText from '@/views/NewSession/PlanVersions/HeaderText.vue'
import HeaderWrapper from '@/views/NewSession/PlanVersions/HeaderWrapper.vue'
import SelectPlanModalContent from '@/views/NewSession/PlanVersions/SelectPlanModalContent.vue'
import {
	formatLastUpdated,
	getPlanFullName,
} from '@/views/NewSession/PlanVersions/utils'
import { computed, onBeforeMount, type PropType, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
	leadId: {
		type: [String, null],
		required: true,
	},
	plan: {
		type: Object as PropType<Partial<PlanData>>,
		required: false,
	},
	index: {
		type: Number,
		required: true,
	},
	autoloadLatestPlan: {
		type: Boolean,
		require: true,
	},
	selectedPriceBook: {
		type: Object as PropType<PriceBook | undefined>,
		required: false,
	},
})

const emit = defineEmits(['planSelected', 'createNewPlan'])
const { t } = useI18n()
const planStore = usePlanStore()
const cloudSyncService = useCloudSyncService()
const importedPlanOptions = ref<PlanOption[]>([])
const loading = ref<boolean>(true)
const localPlan = ref<Partial<PlanData>>()
const showSelectionModal = ref(false)

const selectedPlan = defineModel('selectedPlan', {
	type: [String, null],
	required: false,
})

const selectedPlanOption = computed(() => {
	const importedPlan = importedPlanOptions.value.find(
		(option) => option.id === selectedPlan.value
	)

	return importedPlan ?? optionFromLocal(props.plan!)
})

const handleSelectedPlanChange = async (planOption: PlanOption) => {
	showSelectionModal.value = false
	selectedPlan.value = planOption.id
	await emitPlanSelectionChange(planOption)
}

const retrievePlanBySource = async (
	planId: string,
	source: string
): Promise<Partial<PlanData> | undefined> => {
	if (source === 'cloud-sync') {
		const fetchedPlan = await cloudSyncService.plan.getBasicDataById(planId)
		if (fetchedPlan) {
			return fetchedPlan
		}
	} else if (source === 'local') {
		return await planStore.loadPlan(planId)
	}
	return undefined
}

const setPlanOptions = async () => {
	let planOptions = await getCloudSyncPlanOptions()

	// get local plan options
	const localPlanOptions = await getLocalPlanOptions()
	planOptions.push(...localPlanOptions)

	// sort by date, filter out duplicate ids (when local and cloudSync plan have id collision, always take the newer plan)
	return planOptions
		.sort((a, b) => {
			return Date.parse(b.lastUpdated!) - Date.parse(a.lastUpdated!)
		})
		.filter((option) => planIsNewestVersion(option, planOptions))
}

const planIsNewestVersion = (
	currentOption: PlanOption,
	allOptions: PlanOption[]
) => {
	return !allOptions.some(
		(option) =>
			option.id.toUpperCase() === currentOption.id.toUpperCase() &&
			Date.parse(option.lastUpdated!) > Date.parse(currentOption.lastUpdated!)
	)
}

const optionFromLocal = (plan: Partial<PlanData>): PlanOption => {
	return {
		id: props.plan?.id!,
		fullName: getPlanFullName(plan),
		lastUpdated: props.plan?.lastUpdated,
		formattedDate: formatLastUpdated(props.plan?.lastUpdated),
		source: 'local',
		funeralHomeId: props.plan?.funeralHomeId ?? '',
	}
}

onBeforeMount(async () => {
	if (props.plan) {
		localPlan.value = { ...props.plan }
	}
	importedPlanOptions.value = await setPlanOptions()
	// instead of the first one, select the first one that matches the local plan's name

	if (localPlan.value?.firstName && props.autoloadLatestPlan) {
		const initialValue = importedPlanOptions.value.find((option) =>
			option.fullName?.includes(localPlan.value!.firstName!)
		)
		// Not directly using selectedPlan ref in the emit because of some race conditions
		if (initialValue?.id) {
			await emitPlanSelectionChange(initialValue)
		}
		selectedPlan.value = initialValue?.id
	} else {
		selectedPlan.value = localPlan.value?.id
	}

	loading.value = false
})

const getCloudSyncPlanOptions = async (): Promise<PlanOption[]> => {
	// todo CRM-1739 - the contact id is now attached to the plan (props.plan?.contactId)
	if (!props.leadId) {
		return []
	}
	const cloudSyncPlans = await cloudSyncService.plan.getByLeadId(props.leadId)

	return cloudSyncPlans.map((plan) => {
		return {
			id: plan.id,
			fullName: getPlanFullName(plan.planData.basic),
			lastUpdated: plan.lastUpdated,
			funeralHomeId: plan.planData.basic.funeralHomeId!,
			formattedDate: formatLastUpdated(plan.lastUpdated),
			source: 'cloud-sync',
		}
	})
}

const getLocalPlanOptions = async (): Promise<PlanOption[]> => {
	if (!props.leadId) {
		return []
	}
	const localPlans = await planStore.fetchByLeadId(props.leadId)

	return localPlans.map((plan: StoredPlanData) => {
		const planBasicData = plan.basic

		return {
			id: planBasicData.id,
			fullName: getPlanFullName(planBasicData),
			lastUpdated: planBasicData.lastUpdated,
			funeralHomeId: planBasicData.funeralHomeId ?? '',
			formattedDate: formatLastUpdated(planBasicData.lastUpdated),
			source: 'local',
		}
	})
}

const emitPlanSelectionChange = async (planOption: PlanOption) => {
	const plan = await retrievePlanBySource(
		planOption.id as string,
		planOption.source
	)
	emit('planSelected', {
		index: props.index,
		plan,
		source: planOption.source,
	})
}

const startNewPlan = () => {
	emit('createNewPlan', {
		index: props.index,
		plan: props.plan,
	})
}
</script>
